// Singleton EWMA simulation store — runs outside React, persists across page navigation
import { alertStore } from './alertStore';

const ALPHA          = 0.032;
const CT_BASELINE    = 2.0;
const CT_SENSITIVITY = 0.2;
const S_MID          = 0.001;
const S_LATE         = 0.003;
const SLOPE_WINDOW   = 20;
const VAR_WINDOW     = 50;

export const SENSOR_BASELINE: Record<string, { mu: number; sigma: number; iThreshold: number }> = {
  temp:  { mu: 25,  sigma: 15,  iThreshold: 150 },
  vib:   { mu: 0.5, sigma: 1.5, iThreshold: 15  },
  speed: { mu: 50,  sigma: 25,  iThreshold: 200  },
};

export interface SensorSimState {
  ewma:        number;
  mu:          number;
  sigma:       number;
  iThreshold:  number;
  ewmaHistory: number[];
  varWindow:   number[];
  history:     { t: number; raw: number; ewma: number; ucl2: number; ucl3: number }[];
  stage:       'NORMAL' | 'EARLY' | 'MID' | 'LATE';
  prevStage:   'NORMAL' | 'EARLY' | 'MID' | 'LATE';
  ttf:         number;
  slope:       number;
  variance:    number;
  ct:          number;
}

export interface EWMASimStoreState {
  running:    boolean;
  cycle:      number;
  machineId:  string;
  machineName:string;
  values:     { temp: number; vib: number; speed: number };
  ranges:     { temp: { min: number; max: number }; vib: { min: number; max: number }; speed: { min: number; max: number } };
  sensors:    Record<string, SensorSimState>;
  emitted:    Set<string>;
  lastEmit:   Record<string, number>;
}

function initSensor(key: string): SensorSimState {
  const bl = SENSOR_BASELINE[key] || { mu: 10, sigma: 2, iThreshold: 100 };
  return {
    ewma: bl.mu, mu: bl.mu, sigma: bl.sigma, iThreshold: bl.iThreshold,
    ewmaHistory: [], varWindow: [], history: [],
    stage: 'NORMAL', prevStage: 'NORMAL', ttf: -1, slope: 0, variance: 0, ct: CT_BASELINE,
  };
}

function tickSensor(s: SensorSimState, val: number, t: number): SensorSimState {
  const ns = { ...s, ewmaHistory: [...s.ewmaHistory], varWindow: [...s.varWindow], history: [...s.history] };
  // Asymmetric alpha: slow rise, faster fall
  const effectiveAlpha = val < ns.ewma ? 0.05 : ALPHA;
  ns.ewma = effectiveAlpha * val + (1 - effectiveAlpha) * ns.ewma;
  ns.ewmaHistory.push(ns.ewma);
  if (ns.ewmaHistory.length > VAR_WINDOW + SLOPE_WINDOW) ns.ewmaHistory.shift();
  ns.varWindow.push(val);
  if (ns.varWindow.length > VAR_WINDOW) ns.varWindow.shift();
  const slope = ns.ewmaHistory.length >= SLOPE_WINDOW + 1
    ? (ns.ewma - ns.ewmaHistory[ns.ewmaHistory.length - SLOPE_WINDOW - 1]) / SLOPE_WINDOW : 0;
  const mean = ns.varWindow.length > 0 ? ns.varWindow.reduce((a, b) => a + b, 0) / ns.varWindow.length : ns.mu;
  const variance = ns.varWindow.length > 1
    ? ns.varWindow.reduce((a, b) => a + (b - mean) ** 2, 0) / ns.varWindow.length : 0;
  const ucl2 = ns.mu + 2 * ns.sigma;
  const ucl3 = ns.mu + 3 * ns.sigma;
  const ct = CT_BASELINE + CT_SENSITIVITY * Math.max(0, ns.ewma - ns.mu);
  ns.ttf = slope > 0.001 ? Math.round((ns.iThreshold - ns.ewma) / slope) : -1;
  ns.slope    = +slope.toFixed(5);
  ns.variance = +variance.toFixed(4);
  ns.ct       = +ct.toFixed(3);
  const earlyZoneStart = ns.mu + ns.sigma * 1.5;
  ns.prevStage = ns.stage;
  if (ns.ewma > ucl3 && slope > S_LATE) {
    ns.stage = 'LATE';
  } else if (ns.ewma > ucl2) {
    ns.stage = 'MID';
  } else if (ns.ewma > earlyZoneStart) {
    if (ns.stage === 'NORMAL') ns.stage = 'EARLY';
  } else if (ns.ewma <= earlyZoneStart && slope <= 0) {
    ns.stage = 'NORMAL';
  }
  ns.history.push({ t, raw: +val.toFixed(2), ewma: +ns.ewma.toFixed(3), ucl2: +ucl2.toFixed(3), ucl3: +ucl3.toFixed(3) });
  if (ns.history.length > 80) ns.history.shift();
  return ns;
}

type Listener = () => void;

class EWMASimStore {
  private state: EWMASimStoreState | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private listeners: Set<Listener> = new Set();

  get(): EWMASimStoreState | null { return this.state; }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  private notify() { this.listeners.forEach(fn => fn()); }

  setValues(values: { temp: number; vib: number; speed: number }) {
    if (this.state) {
      this.state.values = { ...values };
      this.notify();
    }
  }

  start(machineId: string, machineName: string,
        values: { temp: number; vib: number; speed: number },
        ranges: EWMASimStoreState['ranges']) {
    this.stop();
    const sensors: Record<string, SensorSimState> = {};
    ['temp', 'vib', 'speed'].forEach(k => { sensors[k] = initSensor(k); });
    this.state = {
      running: true, cycle: 0,
      machineId, machineName,
      values: { ...values }, ranges: { ...ranges },
      sensors, emitted: new Set(), lastEmit: {},
    };
    this.notify();
    this.interval = setInterval(() => this._tick(), 2000);
  }

  pause() {
    if (!this.state) return;
    if (this.interval) { clearInterval(this.interval); this.interval = null; }
    this.state.running = false;
    this.notify();
  }

  resume() {
    if (!this.state || this.state.running) return;
    this.state.running = true;
    this.notify();
    this.interval = setInterval(() => this._tick(), 2000);
  }

  stop() {
    if (this.interval) { clearInterval(this.interval); this.interval = null; }
    this.state = null;
    alertStore.clearAllSlider();
    this.notify();
  }

  private _tick() {
    if (!this.state) return;
    const s = this.state;
    s.cycle += 1;
    const nextCycle = s.cycle;
    const newSensors: Record<string, SensorSimState> = {};

    ['temp', 'vib', 'speed'].forEach(key => {
      const current = s.sensors[key];
      if (!current) return;
      const currentVal = s.values[key as keyof typeof s.values];
      const noise = (Math.random() - 0.5) * (currentVal * 0.03 + 0.2);
      const clamped = Math.max(s.ranges[key as keyof typeof s.ranges].min,
                      Math.min(s.ranges[key as keyof typeof s.ranges].max, currentVal + noise));
      const updated = tickSensor(current, clamped, nextCycle);
      newSensors[key] = { ...updated };

      const stage     = updated.stage;
      const prevStage = updated.prevStage;
      const emitKey   = `${key}-${stage}`;

      // Recovery detection — EWMA dropped back through a threshold
      const STAGE_RANK: Record<string, number> = { NORMAL: 0, EARLY: 1, MID: 2, LATE: 3 };
      if (STAGE_RANK[stage] < STAGE_RANK[prevStage] && prevStage !== 'NORMAL') {
        const recoveryKey = `${key}-recovery-${prevStage}->${stage}`;
        if (!s.emitted.has(recoveryKey)) {
          s.emitted.add(recoveryKey);
          const sliderLabels: Record<string, { label: string; unit: string }> = {
            temp:  { label: 'Temperature', unit: '°C'  },
            vib:   { label: 'Vibration',   unit: 'g'   },
            speed: { label: 'Speed',       unit: 'RPM' },
          };
          const { label, unit } = sliderLabels[key];
          const recoveryMsg =
            stage === 'NORMAL' ? `✅ Recovering to Normal — continue monitoring`
            : stage === 'EARLY'  ? `🔵 Recovering from ${prevStage === 'LATE' ? 'Critical' : 'Medium'} — still in Early zone`
            :                      `🟡 Recovering from Critical — still in Medium zone`;
          const recoverySeverity =
            stage === 'NORMAL' ? 'Low' : stage === 'EARLY' ? 'Low' : 'Medium';
          alertStore.setSliderAlert({
            id: `slider-${s.machineId}-${key}-recovery-${prevStage}-${stage}`,
            source: 'slider' as any,
            stage: stage as any,
            sensorName: label,
            machineName: s.machineName,
            currentValue: +updated.ewma.toFixed(2),
            unit,
            severity: recoverySeverity,
            timestamp: new Date().toLocaleTimeString(),
            recommendedAction: recoveryMsg,
            failureProb: 0,
            rul: 30,
            threshold: { min: 0, max: 100 },
          });
          // Send recovery email via n8n
          try {
            const prefs = JSON.parse(localStorage.getItem('alertPreferences') || '{}');
            const toEmails: string[] = prefs.notificationEmails || [];
            if (toEmails.length > 0) {
              fetch('https://n8n.digitransolutions.in/webhook/twinmaker-send-alert', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  to: toEmails, entity: s.machineName,
                  severity: recoverySeverity,
                  ewmaStage: `RECOVERY_${stage.toUpperCase()}`,
                  scenarioName: 'What-If Simulator — Recovery',
                  trendSummary: recoveryMsg,
                  ttfText: 'Machine recovering — EWMA dropping toward baseline',
                  riskLabel: 'Recovering',
                  cycle: nextCycle,
                  tempVal:  key === 'temp'  ? updated.ewma.toFixed(1) : '—',
                  vibVal:   key === 'vib'   ? updated.ewma.toFixed(2) : '—',
                  speedVal: key === 'speed' ? updated.ewma.toFixed(0) : '—',
                  action: recoveryMsg,
                  timestamp: new Date().toISOString(),
                }),
              }).catch(() => {});
            }
          } catch {}
        }
      }
      // Only fire once per stage per sensor
      if (stage !== 'NORMAL' && !s.emitted.has(emitKey)) {
        s.emitted.add(emitKey);
        const sliderLabels: Record<string, { label: string; unit: string }> = {
          temp:  { label: 'Temperature', unit: '°C'  },
          vib:   { label: 'Vibration',   unit: 'g'   },
          speed: { label: 'Speed',       unit: 'RPM' },
        };
        const { label, unit } = sliderLabels[key];
        const severity = stage === 'LATE' ? 'Critical' : stage === 'MID' ? 'Medium' : 'Low';
        const action   = stage === 'LATE' ? 'Stop equipment immediately and inspect'
                       : stage === 'MID'  ? 'Schedule maintenance within 7 days'
                       :                    'Monitor closely and plan inspection';
        // Send n8n immediately for all stages
        try {
          const prefs = JSON.parse(localStorage.getItem('alertPreferences') || '{}');
          const toEmails: string[] = prefs.notificationEmails || [];
          if (toEmails.length > 0) {
            fetch('https://n8n.digitransolutions.in/webhook/twinmaker-send-alert', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: toEmails, entity: s.machineName, severity,
                ewmaStage: stage, scenarioName: 'What-If Simulator',
                trendSummary: `${label} EWMA crossed ${stage} threshold at cycle ${nextCycle}.`,
                ttfText: 'Based on current drift rate',
                riskLabel: severity === 'Critical' ? 'High' : severity === 'Medium' ? 'Moderate' : 'Low',
                cycle: nextCycle,
                tempVal:  key === 'temp'  ? clamped.toFixed(1) : '—',
                vibVal:   key === 'vib'   ? clamped.toFixed(2) : '—',
                speedVal: key === 'speed' ? clamped.toFixed(0) : '—',
                action, timestamp: new Date().toISOString(),
              }),
            }).catch(() => {});
          }
        } catch {}
        alertStore.setSliderAlert({
          id: `slider-${s.machineId}-${key}-${stage}`,
          source: 'slider' as any,
          stage: stage as any,
          sensorName: label,
          machineName: s.machineName,
          currentValue: +clamped.toFixed(2),
          unit,
          severity,
          timestamp: new Date().toLocaleTimeString(),
          recommendedAction: action,
          failureProb: stage === 'LATE' ? 80 : stage === 'MID' ? 50 : 20,
          rul: stage === 'LATE' ? 1 : stage === 'MID' ? 7 : 30,
          threshold: { min: s.ranges[key as keyof typeof s.ranges].min, max: s.ranges[key as keyof typeof s.ranges].max },
        });
      }
    });

    s.sensors = newSensors;
    this.notify();
  }
}

export const ewmaSimStore = new EWMASimStore();
